import { Resource } from '../interfaces/resource';
import {
    DraasApiAggregatedProtectionGroup,
    DraasApiAwsNetworkConfigurationDetails,
    DraasApiBlackoutWindow,
    DraasApiNetworkConfiguration,
    DraasApiTagFilter,
    DraasApiVmwareNetworkConfigurationDetails,
} from '@pure/paas-api-gateway-client-ts';
import { DisasterRecoveryTag } from './disaster-recovery-tag';
import { Moment } from 'moment';
import moment from 'moment/moment';

export enum GroupProtectionState {
    ENABLED = 'ENABLED',
    ENABLED_PENDING = 'ENABLED_PENDING',
    DISABLED = 'DISABLED',
    DISABLED_PENDING = 'DISABLED_PENDING',
}

export class DisasterRecoveryProtectionGroup implements Resource {
    id: string;
    clusterId: string;
    name: string;
    description: string;
    backupStartTimeUtc: Moment;
    blackoutWindow?: DisasterRecoveryBlackoutWindow;
    isInfrastructureGroup: boolean;
    domainName: string | null;
    isConsistencyGroup: boolean;
    startReplication: 'AFTER_PROTECTION_SUCCESS' | 'IMMEDIATELY';
    hasCloudPreConversion: boolean;
    hasParallelBoot: boolean;
    serviceLevelPolicyId: string;
    serviceLevelPolicyName: string;
    testRecoveryPlanNames: string[];
    version: number;
    numberOfVms: number;
    enrolledVms: string[];
    state: 'SYNCED' | 'DELETED' | 'FAILED' | 'DELETE_FAILED';
    protectionState: GroupProtectionState;
    isInFailover: boolean;
    tagFilter: DisasterRecoveryTagFilter | null;
    isEnrollmentPending: boolean;
    sourceProviderId: string;
    targetProviderIds: Map<number, string>;
    /**
     * This field is provided only for request in relation to recovery plans
     */
    targetNetworkConfigurations: DisasterRecoveryNetworkConfiguration[];
    isProtectionTrigerrable: boolean;
    isUsingLocalRepoAsCache: boolean;

    constructor(json: DraasApiAggregatedProtectionGroup) {
        this.id = json.id;
        this.clusterId = json.cluster_id;
        this.name = json.name;
        this.description = json.description;
        this.backupStartTimeUtc = moment.utc(json.backup_start_time_utc, 'HH:mm:ss');
        this.blackoutWindow = mapDisasterRecoveryBlackoutWindow(json.blackout_window);
        this.isInfrastructureGroup = json.is_infrastructure_group;
        this.domainName = json.domain_name;
        this.isConsistencyGroup = json.is_consistency_group;
        this.startReplication = json.start_replication;
        this.hasCloudPreConversion = json.has_cloud_pre_conversion;
        this.hasParallelBoot = json.has_parallel_boot;
        this.serviceLevelPolicyId = json.service_level_policy_id;
        this.serviceLevelPolicyName = json.service_level_policy_name;
        this.testRecoveryPlanNames = json.test_recovery_plan_names;
        this.version = json.version;
        this.numberOfVms = json.number_of_vms;
        this.enrolledVms = json.enrolled_vms;
        this.state = json.state;
        this.protectionState = GroupProtectionState[json.protection_state] || null;
        this.isInFailover = json.is_in_failover;
        this.tagFilter = json.tag_filter ? new DisasterRecoveryTagFilter(json.tag_filter) : null;
        this.isEnrollmentPending = json.is_enrollment_pending;
        this.sourceProviderId = json.source_provider_id;
        if (json.target_provider_ids) {
            this.targetProviderIds = new Map(
                Object.entries(json.target_provider_ids).map(([ordinal, providerId]) => [
                    parseInt(ordinal),
                    providerId,
                ]),
            );
        }
        this.targetNetworkConfigurations =
            json.target_network_configurations?.map(
                networkConfig => new DisasterRecoveryNetworkConfiguration(networkConfig),
            ) ?? [];
        this.isProtectionTrigerrable = json.is_protection_triggerable ?? true; // TODO: Change default to false once XAAS-16103 is in PRD
        this.isUsingLocalRepoAsCache = json.is_using_local_repo_as_cache ?? false;
    }
}

export interface DisasterRecoveryBlackoutWindow {
    startTimeUtc: string;
    endTimeUtc: string;
}

function mapDisasterRecoveryBlackoutWindow(json?: DraasApiBlackoutWindow): DisasterRecoveryBlackoutWindow {
    if (!json) {
        return undefined;
    }

    return {
        startTimeUtc: json.start_time_utc,
        endTimeUtc: json.end_time_utc,
    };
}

export class DisasterRecoveryTagFilter {
    readonly id: string;
    readonly tagIds: string[];
    readonly tagIdsMode: 'ALL' | 'ANY';
    readonly autoProtect: boolean;
    readonly tags: DisasterRecoveryTag[];

    constructor(json: DraasApiTagFilter) {
        this.id = json.id;
        this.tagIds = json.tag_ids;
        this.tagIdsMode = json.tag_ids_mode;
        this.autoProtect = json.auto_protect;
        this.tags = json.tags.map(tag => new DisasterRecoveryTag(tag));
    }
}

export class DisasterRecoveryNetworkConfiguration {
    siteId: string;
    configurations: (
        | DisasterRecoveryVmwareNetworkConfigurationDetails
        | DisasterRecoveryAwsNetworkConfigurationDetails
    )[];

    constructor(json: DraasApiNetworkConfiguration) {
        this.siteId = json.site_id;
        this.configurations = json.configurations.map(config => {
            if (config.type === 'VmwareNetworkConfigurationDetails') {
                return new DisasterRecoveryVmwareNetworkConfigurationDetails(
                    config as DraasApiVmwareNetworkConfigurationDetails,
                );
            } else if (config.type === 'AwsNetworkConfigurationDetails') {
                return new DisasterRecoveryAwsNetworkConfigurationDetails(
                    config as DraasApiAwsNetworkConfigurationDetails,
                );
            } else {
                return null;
            }
        });
    }
}

export class DisasterRecoveryAwsNetworkConfigurationDetails {
    subnet: string;
    securityGroups: string[];
    /**
     * Allowed options
     * - CIDR 1.2.3.0/24
     * - list of IPs 1.2.3.4, 7.8.9.0
     * - range of IPs 1.2.3.4-1.2.3.14
     * - undefined
     */
    ipAddresses: string[];

    constructor(json: DraasApiAwsNetworkConfigurationDetails) {
        this.subnet = json.subnet;
        this.securityGroups = json.security_groups;
        this.ipAddresses = json.ip_addresses;
    }
}

export class DisasterRecoveryVmwareNetworkConfigurationDetails {
    virtualSwitch: string;
    portGroup: string;
    ipAddresses: string[];
    gateway: string;
    dns?: string[];
    host?: string;

    constructor(json: DraasApiVmwareNetworkConfigurationDetails) {
        this.virtualSwitch = json.virtual_switch;
        this.portGroup = json.port_group;
        this.ipAddresses = json.ip_addresses;
        this.gateway = json.gateway;
        this.dns = json.dns;
        this.host = json.host;
    }
}
